import { CheckCircleFilled, CloseCircleOutlined, InfoCircleFilled, StarFilled } from '@ant-design/icons';
import { App } from '@capacitor/app';
import {
  Purchases,
  PURCHASES_ERROR_CODE,
  PurchasesOffering,
  PurchasesPackage,
  SubscriptionOption,
} from '@revenuecat/purchases-capacitor';
import { useRequest } from 'ahooks';
import IconRound from 'assets/logos/icon-round.svg?react';
import CaregiverAvatarList from 'components/lists/CaregiverAvatarList';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Carousel, CarouselApi, CarouselContent, CarouselDots, CarouselItem } from 'components/ui/carousel';
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import usePreferences from 'hooks/usePreferences';
import useRefreshCurrentUser from 'hooks/useRefreshCurrentUser';
import { cn } from 'lib/utils';
import { XIcon } from 'lucide-react';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { userActions } from 'store/users';
import Platform from 'utils/Platform';

const titleClassName = 'text-center';
const pricingOptionClassName = (selected: boolean) =>
  cn(
    'my-5 flex cursor-pointer flex-row items-center justify-between rounded-md border-2 px-5 py-2.5 [&_p]:m-0',
    selected ? 'border-inverse bg-info-muted shadow-sm' : 'border-background bg-background shadow-xs',
  );
const pricingOptionTitleClassName = 'text-left text-xl font-bold';
const pricingOptionSubtitleClassName = 'text-sm';
const pricingOptionPriceClassName = 'text-right text-sm';
const secondPricePhaseClassName = 'mt-1 text-2xs text-subtle-foreground';
const conditionsClassName = 'text-center text-sm text-subtle-foreground';
const paymentTitleClassName = cn(titleClassName, 'flex items-center justify-center');
const paymentDescriptionClassName = 'my-8 text-center text-base text-subtle-foreground';
const testimonialClassName =
  'mt-12 text-center [&_.anticon]:text-chart-severity-average [&_p]:mt-1 [&_p]:mb-4 [&_p]:italic';

const TESTIMONIAL_AUTOPLAY_MS = 3000;

interface PaywallModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onClose: () => void;
  onFreeMessagesEnabled?: () => void;
  suggestTestIfEligible?: boolean;
}

const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  closeModal,
  onFreeMessagesEnabled,
  suggestTestIfEligible = true,
}) => {
  const { t } = useTranslation();
  const [displayPage, setDisplayPage] = useState<
    'offers' | 'paymentSucceeded' | 'paymentFailed' | 'freeMessagesEnabled'
  >('offers');
  const [purchaseInProgress, setPurchaseInProgress] = useState(false);
  const dispatch = useDispatch();
  const refreshCurrentUser = useRefreshCurrentUser();
  const messagesLimit = 5;

  const [offering, setOffering] = useState<PurchasesOffering | null>(null);
  const [testimonialsCarousel, setTestimonialsCarousel] = useState<CarouselApi>();

  const { run: getOfferings } = useRequest(() => Purchases.getOfferings(), {
    manual: true,
    onSuccess: offerings => {
      setOffering(offerings.all['offering2']);
    },
  });
  const { value: didEnableFreeMessagesValue } = usePreferences(Keys.PAYWALL_DID_ENABLE_FREE_MESSAGES);
  const didEnableFreeMessages = didEnableFreeMessagesValue === true;
  const shouldSuggestFreeTest = !didEnableFreeMessages && messagesLimit > 0 && suggestTestIfEligible;

  const [selectedPricingOption, setSelectedPricingOption] = useState<string>(
    shouldSuggestFreeTest ? 'freeTest' : 'monthly',
  );

  const { run: purchasePackageOrSubscriptionOption } = useRequest(
    ({ pack, option }: { pack?: PurchasesPackage; option?: SubscriptionOption }) => {
      if (pack) {
        return Purchases.purchasePackage({ aPackage: pack });
      }
      if (option) {
        return Purchases.purchaseSubscriptionOption({ subscriptionOption: option });
      }
      throw new Error('No package or option provided');
    },
    {
      manual: true,
      onSuccess: result => {
        setPurchaseInProgress(false);
        // Grants access straight away, from what the SDK just told us.
        dispatch(userActions.setCurrentUserSubscription(result.customerInfo));
        setDisplayPage('paymentSucceeded');
        posthog.capture('paywall.purchase_succeeded', { purchased_item: result.productIdentifier });
        // And pull the user again so the backend's own verdict takes over. The
        // RevenueCat webhook is what turns this purchase into an access tier on
        // our side and it lags the purchase, hence the `until`.
        void refreshCurrentUser({ until: user => user.access_tier !== 'FREE' });
      },
      onError: (error: any) => {
        setPurchaseInProgress(false);
        setDisplayPage('paymentFailed');
        if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
          // Purchase cancelled
          posthog.capture('paywall.purchase_cancelled');
        } else {
          // Error making purchase
          posthog.capture('paywall.purchase_failed');
        }
      },
    },
  );

  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeStatus, setPromoCodeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [activeOffer, setActiveOffer] = useState<{
    package: PurchasesPackage;
    selectedOption: SubscriptionOption;
  } | null>(null); // For Android: the matched offer
  const [iosSyncingPurchases, setIosSyncingPurchases] = useState(false); // For iOS: track syncing state
  const appStateListenerRef = useRef<any>(null); // Track the listener for cleanup

  useEffect(() => {
    if (Platform.isNative()) {
      getOfferings();
    }
  }, []);

  // The prices shown when the paywall opens (what Ant Design's Modal reported
  // through `afterOpenChange`).
  useEffect(() => {
    if (isOpen) {
      posthog.capture('paywall.prices_displayed', {
        monthly_price: offering?.monthly?.product.price,
        monthly_intro_price: offering?.monthly?.product.introPrice?.price,
        three_month_price: offering?.threeMonth?.product.price,
        three_month_intro_price: offering?.threeMonth?.product.introPrice?.price,
        currency: offering?.monthly?.product.currencyCode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The testimonials slide on their own (what `Carousel autoplay` did).
  useEffect(() => {
    if (!testimonialsCarousel) return;
    const interval = setInterval(() => testimonialsCarousel.scrollNext(), TESTIMONIAL_AUTOPLAY_MS);
    return () => clearInterval(interval);
  }, [testimonialsCarousel]);

  // Cleanup app state listener when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
        appStateListenerRef.current = null;
      }
    };
  }, []);

  // Set up app state listener when iOS promo code is active and modal is open
  useEffect(() => {
    if (isOpen && Platform.getPlatform() === 'ios' && iosSyncingPurchases && !appStateListenerRef.current) {
      const listener = App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          try {
            await Purchases.syncPurchases();
            // Get updated customer info after sync
            const result = await Purchases.getCustomerInfo();
            const customerInfo = result.customerInfo;
            // Check if user now has an active subscription
            const hasActiveSubscription = customerInfo.activeSubscriptions.length > 0;
            setIosSyncingPurchases(false); // Stop spinner
            if (hasActiveSubscription) {
              dispatch(userActions.setCurrentUserSubscription(customerInfo));
              void refreshCurrentUser({ until: user => user.access_tier !== 'FREE' });
              setDisplayPage('paymentSucceeded');
              posthog.capture('paywall.purchase_succeeded', {
                source: 'ios_promo_code',
                purchased_item: customerInfo.activeSubscriptions[0],
              });
            } else {
              setDisplayPage('paymentFailed');
              posthog.capture('paywall.purchase_failed', {
                source: 'ios_promo_code',
                reason: 'no_subscription_after_sync',
              });
            }
          } catch (error) {
            console.error('Error syncing purchases:', error);
            setIosSyncingPurchases(false); // Stop spinner
            setDisplayPage('paymentFailed');
            posthog.capture('paywall.purchase_failed', { source: 'ios_promo_code', reason: 'sync_error' });
          }
        }
      });
      appStateListenerRef.current = listener;
    }

    // Cleanup listener when modal closes or syncing stops
    if ((!isOpen || !iosSyncingPurchases) && appStateListenerRef.current) {
      appStateListenerRef.current.remove();
      appStateListenerRef.current = null;
    }
  }, [isOpen, iosSyncingPurchases, dispatch, refreshCurrentUser]);

  const handlePromoCodeSubmit = async () => {
    setPromoCodeStatus('loading');
    setPromoCodeError(null);
    if (Platform.getPlatform() === 'ios') {
      // iOS: Open App Store redeem link
      window.open(
        `https://apps.apple.com/redeem?ctx=offercodes&id=6503453272&code=${encodeURIComponent(promoCode)}`,
        '_blank',
      );
      setPromoCodeStatus('success');
      setIosSyncingPurchases(true); // Start spinner for iOS
      // The app state listener will be set up by the useEffect above
    } else if (Platform.getPlatform() === 'android') {
      // Android: Find offer with matching ID
      let found = false;
      if (offering) {
        // Search all packages in the offering for a matching offerId
        offering.availablePackages.forEach(pack => {
          if (pack.product && pack.product.subscriptionOptions) {
            const match = pack.product.subscriptionOptions.find(
              (option: SubscriptionOption) =>
                option.id &&
                option.id.toLowerCase().includes(':promo-') &&
                option.id.toLowerCase().endsWith(promoCode.toLowerCase()),
            );
            if (match) {
              setActiveOffer({ package: pack, selectedOption: match });
              setPromoCodeStatus('success');
              found = true;
            }
          }
        });
      }
      if (!found) {
        setPromoCodeStatus('error');
        setPromoCodeError(t('paywall.promoCode.invalid'));
        setActiveOffer(null);
      }
    } else {
      setPromoCodeStatus('error');
      setPromoCodeError(t('paywall.promoCode.unsupported'));
    }
    posthog.capture('paywall.promo_code_submitted', {
      promo_code: promoCode,
    });
  };

  const handlePurchase = () => {
    setPurchaseInProgress(true);
    if (selectedPricingOption === 'threeMonth') {
      if (Platform.getPlatform() === 'android' && activeOffer?.package.packageType === 'THREE_MONTH') {
        purchasePackageOrSubscriptionOption({ option: activeOffer.selectedOption });
        posthog.capture('paywall.purchase_started', {
          pricing_option: selectedPricingOption,
          price: offering?.threeMonth?.product.price,
          intro_price: activeOffer.selectedOption.pricingPhases[0]?.price.amountMicros / 1e6,
          currency: offering?.threeMonth?.product.currencyCode,
          promo_code: promoCode,
        });
      } else {
        purchasePackageOrSubscriptionOption({ pack: offering?.threeMonth as PurchasesPackage });
        posthog.capture('paywall.purchase_started', {
          pricing_option: selectedPricingOption,
          price: offering?.threeMonth?.product.price,
          intro_price: offering?.threeMonth?.product.introPrice?.price,
          currency: offering?.threeMonth?.product.currencyCode,
        });
      }
    } else if (selectedPricingOption === 'monthly') {
      if (Platform.getPlatform() === 'android' && activeOffer?.package.packageType === 'MONTHLY') {
        purchasePackageOrSubscriptionOption({ option: activeOffer.selectedOption });
        posthog.capture('paywall.purchase_started', {
          pricing_option: selectedPricingOption,
          price: offering?.monthly?.product.price,
          intro_price: activeOffer.selectedOption.pricingPhases[0]?.price.amountMicros / 1e6,
          currency: offering?.monthly?.product.currencyCode,
          promo_code: promoCode,
        });
      } else {
        purchasePackageOrSubscriptionOption({ pack: offering?.monthly as PurchasesPackage });
        posthog.capture('paywall.purchase_started', {
          pricing_option: selectedPricingOption,
          price: offering?.monthly?.product.price,
          intro_price: offering?.monthly?.product.introPrice?.price,
          currency: offering?.monthly?.product.currencyCode,
        });
      }
    } else {
      purchasePackageOrSubscriptionOption({ pack: offering?.weekly as PurchasesPackage });
      posthog.capture('paywall.purchase_started', {
        pricing_option: selectedPricingOption,
        price: offering?.weekly?.product.price,
      });
    }
  };

  const testimonial = (text: string, author: string) => (
    <CarouselItem className={testimonialClassName}>
      <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />
      <p>
        &quot;{text}&quot; - <span className="text-subtle-foreground">{author}</span>
      </p>
    </CarouselItem>
  );

  const offersDescription = (
    <>
      <div className="mb-5 flex justify-center pt-8">
        <IconRound aria-hidden fill="currentColor" className="text-inverse" height={80} width={80} />
      </div>
      <DialogTitle className={titleClassName}>{t('paywall.title')}</DialogTitle>
      <div className="mt-8 mb-4 flex flex-col justify-around [&_p]:m-1 [&_p]:text-center [&_svg]:text-foreground">
        <p>{t('paywall.subtitle')}</p>
        <p className="flex flex-row justify-center gap-8 text-subtle-foreground [&_svg]:mr-2">
          <span>
            <CheckCircleFilled />
            {t('paywall.advantages.unlimitedMessages')}
          </span>
          <span>
            <CheckCircleFilled />
            {t('paywall.advantages.answersIn24h')}
            <span className="text-sm text-subtle-foreground">&nbsp;*</span>
          </span>
        </p>
        <p className="text-sm text-subtle-foreground">{t('chats.explainer.exceptSundays')}</p>
      </div>
      <CaregiverAvatarList direction="horizontal" />
      <Carousel setApi={setTestimonialsCarousel} opts={{ loop: true }}>
        <CarouselContent>
          {testimonial(t('paywall.testimonials.1'), 'Léa')}
          {testimonial(t('paywall.testimonials.2'), 'Camille')}
          {testimonial(t('paywall.testimonials.3'), 'Céline')}
        </CarouselContent>
        <CarouselDots />
      </Carousel>
      <div className="mt-6 mb-4 rounded-xl p-3 text-center text-subtle-foreground [&_svg]:mr-1">
        <Accordion type="single" collapsible>
          <AccordionItem value="1">
            <AccordionTrigger className="justify-center hover:no-underline">
              <span>
                <InfoCircleFilled />
                {t('paywall.whyPaywall')}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                <Trans i18nKey="paywall.explainWhyPaywall" components={{ br: <br /> }} />
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </>
  );

  const webPaywallContent = (
    <>
      {offersDescription}
      <p className={conditionsClassName}>
        <Trans
          i18nKey="paywall.webPaywall"
          components={{
            appstore: <Link target="_blank" to="https://apps.apple.com/fr/app/gynger/id6503453272" />,
            googleplay: <Link target="_blank" to="https://play.google.com/store/apps/details?id=com.gyngercare.app" />,
          }}
        />
      </p>
    </>
  );

  const offersContent = (
    <>
      {offersDescription}
      <div
        className={pricingOptionClassName(selectedPricingOption === 'oneWeek')}
        onClick={() => setSelectedPricingOption('oneWeek')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('oneWeek');
          }
        }}
      >
        <>
          <div>
            <p className={pricingOptionTitleClassName}>{t('paywall.oneWeekPrice.title')}</p>
            <p className={pricingOptionSubtitleClassName}>{t('paywall.oneWeekPrice.subtitle')}</p>
          </div>
          <p className={pricingOptionPriceClassName}>{offering?.weekly?.product.priceString}</p>
        </>
      </div>
      <div
        className={pricingOptionClassName(selectedPricingOption === 'monthly')}
        onClick={() => setSelectedPricingOption('monthly')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('monthly');
          }
        }}
      >
        {Platform.getPlatform() === 'android' &&
        activeOffer &&
        activeOffer.package.packageType === 'MONTHLY' &&
        activeOffer.selectedOption ? (
          <>
            <div>
              <p className={pricingOptionTitleClassName}>{t('paywall.monthlyPrice.title')}</p>
            </div>
            <p className={pricingOptionPriceClassName}>
              <b>{activeOffer.selectedOption.pricingPhases[0]?.price.formatted}</b> {t('paywall.perMonth')}
              <br />
              <div className={secondPricePhaseClassName}>
                {t('paywall.monthlyPrice.secondPricePhase', {
                  months: activeOffer.selectedOption.pricingPhases[0].billingCycleCount,
                  priceString: activeOffer.package.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : offering?.monthly?.product.introPrice ? (
          <>
            <div>
              <p className={pricingOptionTitleClassName}>{t('paywall.monthlyPrice.title')}</p>
              <p className={pricingOptionSubtitleClassName}>
                {t('paywall.monthlyPrice.offerPercent', {
                  percent: Math.floor(
                    (1 - offering?.monthly?.product.introPrice?.price / offering?.monthly?.product.price) * 100,
                  ),
                })}
              </p>
            </div>
            <p className={pricingOptionPriceClassName}>
              <b>{offering?.monthly?.product.introPrice?.priceString}</b> {t('paywall.perMonth')}
              <br />
              <div className={secondPricePhaseClassName}>
                {t('paywall.monthlyPrice.secondPricePhase', {
                  months: offering?.monthly?.product.introPrice?.cycles,
                  priceString: offering?.monthly?.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : (
          <>
            <p className={pricingOptionTitleClassName}>{t('paywall.monthlyPrice.title')}</p>
            <p className={pricingOptionPriceClassName}>
              {offering?.monthly?.product.priceString} {t('paywall.perMonth')}
            </p>
          </>
        )}
      </div>
      <div
        className={pricingOptionClassName(selectedPricingOption === 'threeMonth')}
        onClick={() => setSelectedPricingOption('threeMonth')}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedPricingOption('threeMonth');
          }
        }}
      >
        {Platform.getPlatform() === 'android' &&
        activeOffer &&
        activeOffer.package.packageType === 'THREE_MONTH' &&
        activeOffer.selectedOption ? (
          <>
            <div>
              <p className={pricingOptionTitleClassName}>{t('paywall.threeMonthPrice.title')}</p>
            </div>
            <p className={pricingOptionPriceClassName}>
              <b>{activeOffer.selectedOption.pricingPhases[0]?.price.formatted}</b> {t('paywall.perQuarter')}
              <br />
              <div className={secondPricePhaseClassName}>
                {t('paywall.threeMonthPrice.secondPricePhase', {
                  months: activeOffer.selectedOption.pricingPhases[0]?.billingCycleCount * 3,
                  priceString: activeOffer.package.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : offering?.threeMonth?.product.introPrice ? (
          <>
            <div>
              <p className={pricingOptionTitleClassName}>{t('paywall.threeMonthPrice.title')}</p>
              <p className={pricingOptionSubtitleClassName}>
                {t('paywall.threeMonthPrice.offerPercent', {
                  percent: Math.floor(
                    (1 - offering?.threeMonth?.product.introPrice?.price / offering?.threeMonth?.product.price) * 100,
                  ),
                })}
              </p>
            </div>
            <p className={pricingOptionPriceClassName}>
              <b>{offering?.threeMonth?.product.introPrice?.priceString}</b> {t('paywall.perQuarter')}
              <br />
              <div className={secondPricePhaseClassName}>
                {t('paywall.threeMonthPrice.secondPricePhase', {
                  months: offering?.threeMonth?.product.introPrice?.cycles * 3,
                  priceString: offering?.threeMonth?.product.priceString,
                })}
                <br />
                {t('paywall.threeMonthPrice.cancelAnytime')}
              </div>
            </p>
          </>
        ) : (
          <>
            <p className={pricingOptionTitleClassName}>{t('paywall.threeMonthPrice.title')}</p>
            <p className={pricingOptionPriceClassName}>
              {offering?.threeMonth?.product.priceString} {t('paywall.perQuarter')}
            </p>
          </>
        )}
      </div>
      {shouldSuggestFreeTest && (
        // What Ant Design's `Badge.Ribbon` did: the plan card with a corner ribbon.
        <div className="relative">
          <Badge className="absolute -top-2 right-4 z-10">{t('paywall.freeTest.ribbon')}</Badge>
          <div
            className={pricingOptionClassName(selectedPricingOption === 'freeTest')}
            onClick={() => setSelectedPricingOption('freeTest')}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                setSelectedPricingOption('oneWeek');
              }
            }}
          >
            <>
              <div>
                <p className={pricingOptionTitleClassName}>{t('paywall.freeTest.title')}</p>
                <p className={pricingOptionSubtitleClassName}>{t('paywall.freeTest.subtitle')}</p>
              </div>
            </>
          </div>
        </div>
      )}
      <p className={conditionsClassName}>{t('paywall.conditions')}</p>
      {!showPromoInput && (
        <p className={conditionsClassName}>
          <a className="cursor-pointer underline" onClick={() => setShowPromoInput(true)}>
            {t('paywall.promoCode.haveCode')}
          </a>
        </p>
      )}
      {showPromoInput && (
        <div className="flex flex-col items-center justify-center gap-2 px-8">
          <Input
            placeholder={t('paywall.promoCode.placeholder')}
            value={promoCode}
            onChange={e => setPromoCode(e.target.value)}
            disabled={promoCodeStatus === 'loading' || iosSyncingPurchases}
            onKeyDown={e => {
              if (e.key === 'Enter') handlePromoCodeSubmit();
            }}
          />
          <Button loading={promoCodeStatus === 'loading' || iosSyncingPurchases} onClick={handlePromoCodeSubmit}>
            {iosSyncingPurchases ? t('paywall.promoCode.syncing') : t('paywall.promoCode.submit')}
          </Button>
          <div className="mt-2 flex flex-col items-center justify-center text-center">
            {promoCodeStatus === 'error' && promoCodeError && (
              <span className="text-chart-severity-very-bad">{promoCodeError}</span>
            )}
            {promoCodeStatus === 'success' && Platform.getPlatform() === 'ios' && !iosSyncingPurchases && (
              <span className="text-foreground">{t('paywall.promoCode.iosSuccess')}</span>
            )}
            {iosSyncingPurchases && Platform.getPlatform() === 'ios' && (
              <span className="text-foreground">{t('paywall.promoCode.iosSyncing')}</span>
            )}
            {promoCodeStatus === 'success' && Platform.getPlatform() === 'android' && (
              <span className="text-foreground">{t('paywall.promoCode.androidSuccess')}</span>
            )}
          </div>
        </div>
      )}
      <p className={conditionsClassName}>
        <Trans i18nKey="paywall.doYouHaveACode" components={{ a: <Link to="/settings" /> }} />
      </p>
      <Button
        size="xl"
        className="w-full"
        onClick={() => {
          if (selectedPricingOption === 'freeTest') {
            onFreeMessagesEnabled?.();
            setDisplayPage('freeMessagesEnabled');
            posthog.capture('paywall.free_messages_enabled');
          } else {
            handlePurchase();
          }
        }}
        loading={purchaseInProgress || iosSyncingPurchases}
      >
        {selectedPricingOption === 'oneWeek'
          ? t('paywall.purchaseButtonOneWeek')
          : selectedPricingOption === 'freeTest'
            ? t('paywall.freeMessagesButton.title')
            : t('paywall.purchaseButton')}
      </Button>
      <p className="mt-5 text-center text-xs text-subtle-foreground [&_a]:text-subtle-foreground">
        <Link target="_blank" to="https://gyngercare.com/mentionslegales/">
          {t('paywall.termsOfUse')}
        </Link>{' '}
        -{' '}
        <Link target="_blank" to="https://gyngercare.com/mentionslegales/">
          {t('paywall.privacyPolicy')}
        </Link>
      </p>
    </>
  );

  const paymentSucceededContent = (
    <>
      <DialogTitle className={cn(paymentTitleClassName, '[&_svg]:ml-4 [&_svg]:text-foreground')}>
        {t('paywall.paymentSucceeded.title')} <CheckCircleFilled />
      </DialogTitle>
      <p className={paymentDescriptionClassName}>{t('paywall.paymentSucceeded.description')}</p>

      <Button
        size="xl"
        className="w-full"
        onClick={() => {
          closeModal();
        }}
      >
        {t('paywall.paymentSucceeded.callToAction')}
      </Button>
    </>
  );

  const paymentFailedContent = (
    <>
      <DialogTitle className={cn(paymentTitleClassName, '[&_svg]:mr-3 [&_svg]:text-chart-severity-very-bad')}>
        <CloseCircleOutlined /> {t('paywall.paymentFailed.title')}
      </DialogTitle>
      <p className={paymentDescriptionClassName}>{t('paywall.paymentFailed.description')}</p>
      <p className={paymentDescriptionClassName}>
        <Trans
          i18nKey="paywall.paymentFailed.needHelp"
          components={{ a: <Link to={`mailto:${t('global.support.supportEmail')}`} /> }}
        />
      </p>
      <Button size="xl" className="w-full" onClick={() => setDisplayPage('offers')}>
        {t('paywall.paymentFailed.callToAction')}
      </Button>
    </>
  );

  const freeMessagesEnabledContent = (
    <>
      <DialogTitle className={cn(paymentTitleClassName, '[&_svg]:ml-4 [&_svg]:text-foreground')}>
        {t('paywall.freeMessagesEnabled.title')} <CheckCircleFilled />
      </DialogTitle>
      <p className={paymentDescriptionClassName}>
        {t('paywall.freeMessagesEnabled.description', { count: messagesLimit })}
      </p>

      <Button
        size="xl"
        className="w-full"
        onClick={() => {
          closeModal();
        }}
      >
        {t('paywall.freeMessagesEnabled.callToAction')}
      </Button>
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          onClose();
          posthog.capture('paywall.closed');
        }
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="block max-sm:h-dvh max-sm:max-h-dvh max-sm:w-screen max-sm:max-w-none max-sm:rounded-none max-sm:border-0 sm:max-w-lg"
      >
        {/* The content has its own title per page; the close control keeps its localised name. */}
        <DialogClose asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('global.buttons.close')}
            className="absolute top-4 right-4 z-10"
          >
            <XIcon aria-hidden />
          </Button>
        </DialogClose>
        {displayPage === 'offers' && <>{Platform.getPlatform() === 'web' ? webPaywallContent : offersContent}</>}
        {displayPage === 'paymentSucceeded' && paymentSucceededContent}
        {displayPage === 'paymentFailed' && paymentFailedContent}
        {displayPage === 'freeMessagesEnabled' && freeMessagesEnabledContent}
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
